import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { isTestPreview, phCapture } from "@/lib/posthog-events";
import { getUtmParams } from "@/lib/utm";

const planRank: Record<string, number> = {
  free_trial: 0,
  menu: 1,
  starter: 2,
  pedidos: 2,
  pro: 3,
  operacao: 3,
};

export default function PostHogInit() {
  const posthog = usePostHog();
  const location = useLocation();
  const { user } = useAuth();
  const { plan, status, isLoading: subLoading } = useSubscription();
  const lastPlanRef = useRef<string | null>(null);
  const lastStatusRef = useRef<string | null>(null);

  // Track SPA page views on route change
  useEffect(() => {
    if (!posthog.__loaded || isTestPreview()) return;
    posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
  }, [location.pathname, location.search, posthog]);

  // Capture initial UTM params as super properties (set once, never overwrite)
  useEffect(() => {
    if (!posthog.__loaded) return;
    const utm = getUtmParams();
    if (utm && Object.values(utm).some(Boolean)) {
      const props: Record<string, string> = {};
      if (utm.utm_source) props.initial_utm_source = utm.utm_source;
      if (utm.utm_medium) props.initial_utm_medium = utm.utm_medium;
      if (utm.utm_campaign) props.initial_utm_campaign = utm.utm_campaign;
      if (utm.utm_content) props.initial_utm_content = utm.utm_content;
      if (utm.utm_term) props.initial_utm_term = utm.utm_term;
      if (utm.referrer) props.initial_referrer = utm.referrer;
      posthog.register_once(props);
    }
  }, [posthog]);

  // Identify user with enriched properties; reset on logout
  useEffect(() => {
    if (!posthog.__loaded) return;
    if (user) {
      const utm = getUtmParams();
      const personProps: Record<string, string> = { email: user.email ?? "" };
      if (plan) personProps.plan = plan;
      if (status) personProps.subscription_status = status;
      if (utm?.utm_source) personProps.utm_source = utm.utm_source;
      if (utm?.utm_medium) personProps.utm_medium = utm.utm_medium;
      if (utm?.utm_campaign) personProps.utm_campaign = utm.utm_campaign;
      if (utm?.utm_content) personProps.utm_content = utm.utm_content;
      if (utm?.utm_term) personProps.utm_term = utm.utm_term;
      if (utm?.referrer) personProps.referrer = utm.referrer;

      const personPropsOnce: Record<string, string> = {};
      if (user.created_at) personPropsOnce.signup_date = user.created_at;

      posthog.identify(user.id, personProps, personPropsOnce);
    } else {
      posthog.reset();
      lastPlanRef.current = null;
      lastStatusRef.current = null;
    }
  }, [user, plan, status, posthog]);

  // Detect subscription transitions and fire discrete events
  useEffect(() => {
    if (!posthog.__loaded || !user || subLoading) return;

    const prevPlan = lastPlanRef.current;
    const prevStatus = lastStatusRef.current;

    // First observation after login — record baseline, don't fire events
    if (prevPlan === null && prevStatus === null) {
      lastPlanRef.current = plan;
      lastStatusRef.current = status;
      return;
    }

    if (prevStatus !== status) {
      if (prevStatus === "trialing" && status === "active") {
        phCapture("trial_converted", { plan, prev_plan: prevPlan });
      } else if (status === "cancelled" || status === "expired") {
        phCapture("subscription_cancelled", { plan, prev_plan: prevPlan, prev_status: prevStatus, new_status: status });
      }
    } else if (prevPlan !== plan && status === "active") {
      const prevRank = planRank[prevPlan ?? ""] ?? -1;
      const newRank = planRank[plan ?? ""] ?? -1;
      if (newRank > prevRank) {
        phCapture("plan_upgraded", { from_plan: prevPlan, to_plan: plan });
      } else if (newRank < prevRank) {
        phCapture("plan_downgraded", { from_plan: prevPlan, to_plan: plan });
      }
    }

    lastPlanRef.current = plan;
    lastStatusRef.current = status;
  }, [plan, status, user, subLoading, posthog]);

  return null;
}
